import { useState, useMemo } from "react";
import { useAuth } from "../../../context";
import { useTranslation } from "react-i18next";
import useFetchObjects from "../../../api/useFetchObjects";
import axios from "axios";
import { toast } from "react-toastify";
import {
  Box,
  Paper,
  Typography,
  Button,
  Card,
  CardActionArea,
  CardContent,
  TextField,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Checkbox,
  CircularProgress,
} from "@mui/material";
import { Save as SaveIcon, CheckBox as AllPresentIcon, CheckBoxOutlineBlank as AllAbsentIcon } from "@mui/icons-material";

export default function TopicStudiedPage() {
  const { t } = useTranslation();
  const auth = useAuth();
  const token = auth?.user?.token || "";
  const [selectedSubject, setSelectedSubject] = useState(null);
  const [lectureDate, setLectureDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [titleOfTopics, setTitleOfTopics] = useState("");
  const [skillsTaught, setSkillsTaught] = useState("");
  const [description, setDescription] = useState("");
  const [attendanceMap, setAttendanceMap] = useState({});
  const [saving, setSaving] = useState(false);

  const { data: mySubjectsData } = useFetchObjects(
    ["my-subjects"],
    "subjects/my-subjects/",
    token
  );
  const mySubjects = useMemo(() => {
    const raw = Array.isArray(mySubjectsData) ? mySubjectsData : mySubjectsData?.results ?? [];
    return raw;
  }, [mySubjectsData]);

  const { data: enrollmentsData } = useFetchObjects(
    ["semester-enrollments-subject", selectedSubject?.semester],
    selectedSubject?.semester ? `semester-enrollments/?semester=${selectedSubject.semester}&page_size=500` : "",
    token,
    { enabled: !!selectedSubject?.semester }
  );
  const enrolledStudents = useMemo(() => {
    const raw = enrollmentsData?.results ?? enrollmentsData ?? [];
    const list = Array.isArray(raw) ? raw : [];
    return list.map((e) => ({ id: e.academic ?? e.academic_id, name_display: e.academic_display || `#${e.academic ?? e.academic_id}` }));
  }, [enrollmentsData]);

  const handleToggle = (academicId) => {
    setAttendanceMap((prev) => ({ ...prev, [academicId]: !prev[academicId] }));
  };
  const markAll = (value) => {
    const next = {};
    enrolledStudents.forEach((a) => { next[a.id] = value; });
    setAttendanceMap(next);
  };

  const handleSave = async () => {
    if (!selectedSubject) return;
    setSaving(true);
    try {
      const attendances = enrolledStudents.map((a) => ({
        academic: a.id,
        present: !!attendanceMap[a.id],
      }));
      await axios.post(
        `${process.env.REACT_APP_API_URL}/lectures/take-attendance/`,
        {
          subject: selectedSubject.id,
          lecture_date: lectureDate,
          conducted_by: auth?.user?.user?.id || null,
          title_of_topics: titleOfTopics,
          skills_taught: skillsTaught,
          description,
          attendances,
        },
        { headers: token ? { Authorization: `Token ${token}` } : {} }
      );
      toast.success(t("academic.lectureAttendanceSaved", "Lecture and attendance saved."));
      setAttendanceMap({});
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || t("academic.saveFailed", "Failed to save."));
    } finally {
      setSaving(false);
    }
  };

  if (mySubjects.length === 0) {
    return (
      <Box sx={{ p: 3 }}>
        <Paper sx={{ p: 3, textAlign: "center" }}>
          <Typography variant="h6" color="text.secondary">{t("academic.noAssignedSubjects", "You have no subjects assigned as mentor. Ask an admin to assign you as mentor to a subject in Curriculum.")}</Typography>
        </Paper>
      </Box>
    );
  }

  if (!selectedSubject) {
    return (
      <Box sx={{ p: 3 }}>
        <Paper sx={{ p: 3 }}>
          <Typography variant="h5" sx={{ fontWeight: "bold", mb: 2 }}>{t("academic.topicStudied", "Topic studied")}</Typography>
          <Typography color="text.secondary" sx={{ mb: 2 }}>{t("academic.selectSubjectToEnterTopic", "Select a subject to enter topics, skills and take attendance.")}</Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
            {mySubjects.map((sub) => (
              <Card key={sub.id} sx={{ minWidth: 200 }}>
                <CardActionArea onClick={() => setSelectedSubject(sub)} sx={{ p: 2 }}>
                  <CardContent>
                    <Typography variant="h6">{sub.name}</Typography>
                    <Typography variant="body2" color="text.secondary">{sub.semester_display || sub.term_display || ""}</Typography>
                  </CardContent>
                </CardActionArea>
              </Card>
            ))}
          </Box>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Paper sx={{ p: 3 }}>
        <Button sx={{ mb: 2 }} onClick={() => setSelectedSubject(null)}>{t("common.back", "Back")}</Button>
        <Typography variant="h5" sx={{ fontWeight: "bold", mb: 2 }}>{selectedSubject.name} – {t("academic.enterTopicAndAttendance", "Enter topic & attendance")}</Typography>

        <TextField type="date" label={t("academic.lectureDate", "Lecture date")} value={lectureDate} onChange={(e) => setLectureDate(e.target.value)} InputLabelProps={{ shrink: true }} size="small" sx={{ mr: 2, mb: 2 }} />
        <Typography variant="subtitle2" sx={{ fontWeight: "bold", mt: 2 }}>{t("academic.titleOfTopics", "Title of topics")}</Typography>
        <TextField fullWidth multiline rows={2} value={titleOfTopics} onChange={(e) => setTitleOfTopics(e.target.value)} size="small" sx={{ mb: 2 }} placeholder={t("academic.topicsPlaceholder", "Topics covered")} />
        <Typography variant="subtitle2" sx={{ fontWeight: "bold" }}>{t("academic.skillsTaught", "Skills taught")}</Typography>
        <TextField fullWidth multiline rows={2} value={skillsTaught} onChange={(e) => setSkillsTaught(e.target.value)} size="small" sx={{ mb: 2 }} placeholder={t("academic.skillsPlaceholder", "Skills taught")} />
        <Typography variant="subtitle2" sx={{ fontWeight: "bold" }}>{t("academic.description", "Description")}</Typography>
        <TextField fullWidth multiline rows={2} value={description} onChange={(e) => setDescription(e.target.value)} size="small" sx={{ mb: 2 }} />

        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: "bold" }}>{t("academic.attendance", "Attendance")}</Typography>
          <Button size="small" startIcon={<AllPresentIcon />} onClick={() => markAll(true)}>{t("academic.allPresent", "All present")}</Button>
          <Button size="small" startIcon={<AllAbsentIcon />} onClick={() => markAll(false)}>{t("academic.allAbsent", "All absent")}</Button>
        </Box>

        {enrolledStudents.length === 0 ? (
          <Typography color="text.secondary">{t("academic.noStudentsEnrolled", "No students enrolled in this semester.")}</Typography>
        ) : (
          <Table size="small" sx={{ mb: 2 }}>
            <TableHead><TableRow sx={{ bgcolor: "grey.200" }}>
              <TableCell><strong>{t("academic.student", "Student")}</strong></TableCell>
              <TableCell align="center" width={100}><strong>{t("academic.present", "Present")}</strong></TableCell>
            </TableRow></TableHead>
            <TableBody>
              {enrolledStudents.map((a) => (
                <TableRow key={a.id} hover>
                  <TableCell>{a.name_display}</TableCell>
                  <TableCell align="center"><Checkbox checked={!!attendanceMap[a.id]} onChange={() => handleToggle(a.id)} /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}

        <Button variant="contained" startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />} onClick={handleSave} disabled={saving || enrolledStudents.length === 0}>
          {saving ? t("common.saving", "Saving...") : t("common.save", "Save")}
        </Button>
      </Paper>
    </Box>
  );
}
